import { Variant } from "@components/organisms/VariantsSection";
import {
  VariantComparisonResponse,
  VariantData,
  VariantSpecifications,
} from "@services/variantSpecificationsService";
import { formatSpecificationValue } from "@helpers/specificationUnitFormatter";

// Define the interfaces for the specifications section
interface Feature {
  title: string;
  subtitle?: string;
  tooltip?: string;
  included: boolean;
}

interface FeatureCategory {
  title: string;
  features: Feature[];
}

interface SpecificationVariant {
  id: string;
  name: string;
  categories: FeatureCategory[];
}

const isNotApplicable = (value?: string) => value === "Not Applicable";

// Helper function to capitalize category names
const capitalizeCategoryName = (category: string): string => {
  const categoryMap: { [key: string]: string } = {
    safety: "Safety",
    exterior: "Exterior",
    interior: "Interior",
    tech: "Technology",
    comfort: "Comfort",
    engine: "Engine",
    dimensions: "Dimensions",
  };
  return (
    categoryMap[category] ||
    category.charAt(0).toUpperCase() + category.slice(1)
  );
};

// Transform API specifications to component format
const transformApiSpecifications = (
  specifications: VariantSpecifications
): FeatureCategory[] => {
  const categories: FeatureCategory[] = [];


  Object.entries(specifications).forEach(([categoryKey, categoryData]) => {
    const features: Feature[] = [];
    // Add included features
    categoryData.included.forEach((item) => {
      const title = item.name || item.description;
      features.push({
        title,
        tooltip: item.description || undefined,
        subtitle: formatSpecificationValue(title, item.value),
        included: true,
      });
    });

    // Add not included features
    categoryData.notIncluded.forEach((item) => {
      const title = item.name || item.description;
      features.push({
        title,
        tooltip: item.description || undefined,
        subtitle: formatSpecificationValue(title, item.value),
        included: false,
      });
    });

    const applicableFeatures = features.filter(
      (feature) => !isNotApplicable(feature.subtitle)
    );

    if (applicableFeatures.length > 0) {
      categories.push({
        title: capitalizeCategoryName(categoryKey),
        features: applicableFeatures,
      });
    }
  });
  return categories;
};

// Transform API variant data to component format
const transformVariantData = (
  variantData: VariantData
): SpecificationVariant => {
  return {
    id: variantData?.variantId,
    name: variantData?.variantName,
    categories: variantData?.specifications ? transformApiSpecifications(variantData?.specifications) : [],
  };
};

// Main factory function to transform API response
export const createVariantSpecificationsFromApi = (
  apiResponse: VariantComparisonResponse
): SpecificationVariant[] => {
  const variants: SpecificationVariant[] = [];

  // Add current variant
  variants.push(transformVariantData(apiResponse.current));

  // Add next variant if exists
  if (apiResponse.next) {
    variants.push(transformVariantData(apiResponse.next));
  }

  // Add previous variant if exists
  if (apiResponse.previous) {
    variants.push(transformVariantData(apiResponse.previous));
  }

  return variants;
};

// Legacy function for backward compatibility (now deprecated)
export const createVariantSpecifications = (
  allVariantSlugs: any
): SpecificationVariant[] => {
  console.warn(
    "createVariantSpecifications is deprecated. Use createVariantSpecificationsFromApi instead."
  );
  return [];
};

export type { SpecificationVariant, FeatureCategory, Feature };
